import AWS from 'aws-sdk';
import axios from 'axios';
import useRefreshTokenInterval from 'contexts/token';
import jwt from 'jsonwebtoken';
import queryString from 'query-string';
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import accountService from 'services/account';
import courseService from 'services/course';
import userService from 'services/user';
import { clearAuthSession } from 'utils/auth';
const AuthContext = createContext();
const useAuth = () => useContext(AuthContext);
const ACTIVE_ROSTER_SYNC_STATUSES = ['queued', 'running'];
const PENDING_AUTO_ROSTER_SYNC_KEY = 'pending_auto_roster_sync';

const AuthProvider = (props) => {
    const history = useHistory();
    const location = useLocation();
    const [auth, setAuth] = useState();
    const [currentUser, setCurrentUser] = useState();
    const [loading, setLoading] = useState(true);
    const [, setAwsCredentialsIdentityId] = useState(null);

    const cachedCredentials = useRef(null);
    const tokenCache = useRef(new Map());

    const updateCurrentUser = async (data) => {
        const updateUser = await userService.updateUser(auth.user_id, data);
        setCurrentUser(updateUser);
        return updateUser;
    };

    const logout = useCallback(
        async ({ isExpired = false }) => {
            clearAuthSession();
            setAuth(undefined);
            if (isExpired) {
                setLoading(false);
                return history.replace('/session-expired');
            }
            return history.push('/');
        },
        [history]
    );

    const sessionExpired = useCallback(() => logout({ isExpired: true }), [logout]);

    const getCurrentUser = () => currentUser || null;

    const updateNotificationPreferences = async (newNotification) => {
        try {
            setAuth((auth) => ({
                ...auth,
                notification: newNotification,
            }));
            await accountService.updateAccount(auth.owner_account_id, { notification: newNotification });
        } catch (e) {
            console.error(e);
        }
    };

    const updateProfile = async (data) => {
        try {
            await userService.updateUser(currentUser.id, data);
            return true;
        } catch (e) {
            return false;
        }
    };

    const changeAccount = () => {
        setAuth((current) => ({ iat: current.iat, user_id: current.user_id, username: current.username }));
        return history.push('/');
    };

    const getAccount = async () => {
        if (!auth) return;
        return await accountService.findAccount(auth.owner_account_id);
    };

    const selectAccount = async (accountId, course) => {
        try {
            setLoading(true);
            const res = await accountService.selectAccount(accountId, {
                owner_account_id: accountId,
                accessToken: localStorage.getItem('access_token'),
                course_data: course,
            });
            const { auth_token: token } = res;
            if (!res.has_access) {
                setLoading(false);
                throw new Error('No access to department');
            }
            localStorage.setItem('auth_token', token);
            axios.defaults.headers.common.Authorization = `Bearer ${token}`;
            const cachedUser = jwt.decode(token);
            const accountData = await accountService.findAccount(cachedUser.owner_account_id);
            const courseData = await courseService.findCourse(accountData.course_id);
            const rosterSyncCourse = res.course || courseData;
            const isRosterSyncActive = ACTIVE_ROSTER_SYNC_STATUSES.includes(rosterSyncCourse?.roster_sync_status);
            const shouldTrackPendingRosterSync =
                accountData?.course_id && (res.roster_sync_triggered || isRosterSyncActive);

            if (shouldTrackPendingRosterSync) {
                sessionStorage.setItem(
                    PENDING_AUTO_ROSTER_SYNC_KEY,
                    JSON.stringify({
                        courseId: `${accountData.course_id}`,
                        triggeredOn:
                            rosterSyncCourse?.roster_sync_started_on ||
                            rosterSyncCourse?.roster_sync_heartbeat_on ||
                            Date.now(),
                    })
                );
            } else {
                sessionStorage.removeItem(PENDING_AUTO_ROSTER_SYNC_KEY);
            }
            setAuth({ ...cachedUser, ...accountData });

            if (
                !courseData?.finished_course_set_up &&
                accountData?.role !== 'student' &&
                courseData?.created_on > new Date('2021-12-01').getTime()
            ) {
                history.push('/course-set-up');
            } else if (
                accountData?.role === 'instructor' ||
                accountData?.role === 'ta' ||
                accountData?.role === 'student'
            ) {
                history.push('/assignments');
            } else {
                history.push('/dashboard');
            }
            setLoading(false);
        } catch (e) {
            console.error(e);
            throw e;
        }
    };

    const AWS_IDENTITY_POOL_ID = process.env.REACT_APP_AWS_IDENTITY_POOL_ID;

    // Fetch or return cached account token
    const getCachedAccountToken = useCallback(async (ownerAccountId) => {
        const cachedToken = tokenCache.current.get(ownerAccountId);

        if (cachedToken && cachedToken.expiresAt > Date.now()) {
            return cachedToken;
        }

        const accountTokens = await accountService.getAccountTokens(ownerAccountId);
        const tokenExpiryTime = Date.now() + 10 * 60 * 1000; // Cognito developer token defaults to 15 minutes

        tokenCache.current.set(ownerAccountId, {
            IdentityId: accountTokens.IdentityId,
            Token: accountTokens.Token,
            expiresAt: tokenExpiryTime,
        });

        return accountTokens;
    }, []);

    // Fetch or return cached AWS credentials
    const getAwsCredentials = useCallback(
        async (identityId) => {
            if (
                cachedCredentials.current?.identityId === identityId &&
                !cachedCredentials.current.credentials.needsRefresh()
            ) {
                return cachedCredentials.current.credentials;
            }

            const accountTokens = await getCachedAccountToken(identityId);
            const credentials = new AWS.CognitoIdentityCredentials({
                IdentityPoolId: AWS_IDENTITY_POOL_ID,
                IdentityId: accountTokens.IdentityId,
                Logins: { 'cognito-identity.amazonaws.com': accountTokens.Token },
            });

            await credentials.getPromise(); // Ensure credentials are ready
            cachedCredentials.current = { identityId, credentials };
            return credentials;
        },
        [AWS_IDENTITY_POOL_ID, getCachedAccountToken]
    );

    useEffect(() => {
        const identityId = auth?.owner_account_id || auth?.user_id;
        if (!identityId) return;

        let intervalId;
        let cancelled = false;
        let running = false; // prevent overlap
        const REFRESH_MS = 30 * 60 * 1000;

        const updateAWSConfig = async () => {
            if (running) return;
            running = true;
            try {
                AWS.config.update({ region: process.env.REACT_APP_AWS_REGION });

                const c = await getAwsCredentials(identityId);
                if (cancelled) return;

                AWS.config.update({
                    credentials: new AWS.Credentials(c.accessKeyId, c.secretAccessKey, c.sessionToken),
                });
                setAwsCredentialsIdentityId(identityId);
            } catch (e) {
                console.error('Failed to refresh AWS creds:', e);
            } finally {
                running = false;
            }
        };

        // initial fetch
        updateAWSConfig();

        // every 1 minute
        intervalId = setInterval(() => {
            updateAWSConfig().catch(console.error);
        }, REFRESH_MS);

        return () => {
            cancelled = true;
            clearInterval(intervalId);
        };
    }, [auth?.owner_account_id, auth?.user_id, getAwsCredentials]);

    useEffect(() => {
        const getCurrentSession = async () => {
            // API launch owns the OAuth exchange and any retained LTI token on this route.
            if (location.pathname === '/api-launch') return;

            let cachedUser = null;

            let token = localStorage.getItem('auth_token');
            const access_token = localStorage.getItem('access_token');
            if (location.pathname === '/lti-launch') {
                const { token: ltiLaunchToken } = queryString.parse(location.search);
                localStorage.removeItem('auth_token');
                if (ltiLaunchToken) {
                    localStorage.setItem('auth_token', ltiLaunchToken);
                    token = ltiLaunchToken;
                } else {
                    return history.push('/lti-incomplete');
                }
            }
            if (token) {
                setLoading(true);
                cachedUser = jwt.decode(token);
                axios.defaults.headers.common.Authorization = `Bearer ${token}`;
                if (cachedUser?.owner_account_id) {
                    const accountData = await accountService.findAccount(cachedUser.owner_account_id);
                    if (accountData?.active === false) return await logout({ isExpired: true });
                    setAuth({ ...cachedUser, ...accountData });
                    const userData = await userService.getUser(cachedUser.user_id);
                    setCurrentUser(userData);
                    if (accountData?.course_id) {
                        const existingCourseData = await courseService.findCourse(accountData?.course_id);

                        if (cachedUser.role === 'instructor' && !existingCourseData?.finished_course_set_up) {
                            history.push('/course-set-up');
                        }

                        if (userData && cachedUser?.role && cachedUser.role !== 'admin' && !userData.profile_set_up) {
                            history.push('/profile-setup');
                        }
                    }
                } else {
                    if (cachedUser?.user_id) {
                        const userData = await userService.getUser(cachedUser.user_id);
                        setCurrentUser(userData);
                        setAuth({ ...cachedUser, ...userData });
                        if (userData && !userData.profile_set_up) {
                            history.push('/profile-setup');
                        }
                    } else {
                        if (!token || !access_token) {
                            return history.push('/lti-incomplete');
                        }
                    }
                }
            }
        };

        if (!auth) {
            getCurrentSession()
                .catch((error) => {
                    console.error('Failed to restore auth session:', error);
                    return logout({ isExpired: true });
                })
                .finally(() => setLoading(false));
        }
    }, [auth, history, location.pathname, location.search, logout]);

    const handleRefreshFailed = useCallback(
        (err) => {
            console.error('Token refresh failed:', err);
            sessionExpired();
        },
        [sessionExpired]
    );

    useRefreshTokenInterval({
        auth,
        onRefreshFailed: handleRefreshFailed,
    });

    return (
        <AuthContext.Provider
            value={{
                loading,
                auth,
                currentUser,
                setAuth,
                getCurrentUser,
                setCurrentUser,
                updateCurrentUser,
                updateNotificationPreferences,
                updateProfile,
                logout,
                changeAccount,
                sessionExpired,
                getAccount,
                selectAccount,
            }}
            {...props}
        >
            {props.children}
        </AuthContext.Provider>
    );
};

export { useAuth };
export default AuthProvider;
